var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// components/ge-tabs.ts
import { DesignSystem } from "@adornis/chemistry/elements/theming/design";
import { BehaviorController } from "@adornis/chemistry/controllers/behavior-controller";
import { SizeController } from "@adornis/chemistry/controllers/size-controller";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element";
import { css } from "@adornis/chemistry/directives/css";
import "@adornis/chemistry/elements/components/x-card";
import "@adornis/chemistry/elements/components/x-icon";
import "@adornis/chemistry/elements/components/x-flex";
import { xComponents } from "@adornis/chemistry/elements/x-components";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { distinctUntilChanged, takeUntil } from "rxjs";
var ElementsTabsGermanium = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.styles = () => ({
      ":host": { position: "relative", overflowX: "auto", maxWidth: "100%" }
    });
    this.size = new SizeController(this);
    this.selected = new BehaviorController(this, -1);
    this.marker = { startPosition: 0, width: 0 };
    this.selectedColor = "";
  }
  firstUpdated() {
    this.selected.pipe(distinctUntilChanged(), takeUntil(this.disconnected)).subscribe((selected) => {
      const labels = Array.from(this._slot.assignedNodes()).filter(
        (assignedNode) => assignedNode.nodeName && assignedNode.nodeName !== "#text"
      );
      const selectedLabel = labels[selected];
      requestAnimationFrame(() => {
        if (selectedLabel) {
          if (this.marker.startPosition !== selectedLabel?.offsetLeft || this.marker.width !== selectedLabel?.offsetWidth)
            this.marker = { startPosition: selectedLabel?.offsetLeft || 0, width: selectedLabel?.offsetWidth ?? 0 };
        } else if (this.marker.startPosition !== 0 || this.marker.width !== 0)
          this.marker = { startPosition: 0, width: 0 };
      });
      labels.filter((label) => label !== selectedLabel).forEach((label) => label.removeAttribute("selected"));
      selectedLabel?.setAttribute("selected", "");
    });
  }
  render() {
    return xComponents(html`
      <x-flex horizontal ${css({ position: "relative" })}>
        <slot id="labels" ${ref((slot) => this._slot = slot)}></slot>
        ${this.markerElement()}
      </x-flex>
    `);
  }
  markerElement() {
    return html`
      <div
        ${css({
      height: "8px",
      background: this.selectedColor ? this.selectedColor : this.colors.accent,
      width: "100px",
      borderRadius: "50px",
      position: "absolute",
      bottom: "0",
      left: "0",
      transformOrigin: "left",
      transform: `translateX(${this.marker.startPosition}px) scaleX(${this.marker.width / 100})`,
      transition: "transform .2s"
    })}
      ></div>
    `;
  }
};
__decorateClass([
  state()
], ElementsTabsGermanium.prototype, "size", 2);
__decorateClass([
  state()
], ElementsTabsGermanium.prototype, "selected", 2);
__decorateClass([
  state()
], ElementsTabsGermanium.prototype, "marker", 2);
__decorateClass([
  state()
], ElementsTabsGermanium.prototype, "selectedColor", 2);
ElementsTabsGermanium = __decorateClass([
  customElement("ge-tabs")
], ElementsTabsGermanium);
var ElementsTabGermanium = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.selectedColor = "";
    this.icon = "";
  }
  styles() {
    return {
      ":host": {
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "row",
        flex: "1",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        borderBottom: "2px solid transparent",
        padding: this.spacing.md,
        // height: `calc(${sizes.topbarDesktop} - 1px)`,
        userSelect: "none"
      },
      ":host([selected])": { color: this.selectedColor ? this.selectedColor : this.colors.accent },
      ":host([secondary])": { fontWeight: "lighter", fontSize: "12px" },
      ":host([big])": { height: "70px" },
      // '@media (max-width: 992px)': { height: '50px' },
      [`${DesignSystem.prefix}-text`]: { width: "unset" },
      "@media screen and (max-width: 500px)": {
        [`${DesignSystem.prefix}-text`]: { fontSize: "14px" }
      }
    };
  }
  updated(changedProps) {
    super.updated(changedProps);
    this._updateTextContents();
  }
  render() {
    return xComponents(html`
      <x-flex crossaxis-center center horizontal ${css({ height: "100%" })}>
        ${this.icon ? html` <x-icon ${css({ marginRight: this.spacing.sm })}> ${this.icon} </x-icon> ` : nothing}
        <x-text
          ${css({ color: "white", fontSize: "16px", lineHeight: "32px" })}
          tone="subtle"
          ellipsis
          id="text"
          ${ref((text) => this._text = text)}
        >
        </x-text>
        <div ${css({ display: "none" })}>
          <slot
            ${ref((slot) => this._slot = slot)}
            @slotchange=${() => {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = void 0;
      }
      this._observer = new MutationObserver(() => this._updateTextContents());
      this._slot?.assignedNodes({ flatten: true }).forEach((node) => {
        this._observer.observe(node, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
      });
      this._updateTextContents();
    }}
          ></slot>
        </div>
      </x-flex>
    `);
  }
  _updateTextContents() {
    const textNode = this._text;
    if (textNode) {
      textNode.value = "";
      this._slot?.assignedNodes({ flatten: true }).forEach((node) => {
        if (node.nodeType === 3)
          textNode.value = textNode.value + " " + node.textContent;
      });
    }
  }
};
__decorateClass([
  state()
], ElementsTabGermanium.prototype, "selectedColor", 2);
__decorateClass([
  property()
], ElementsTabGermanium.prototype, "icon", 2);
ElementsTabGermanium = __decorateClass([
  customElement("ge-tab")
], ElementsTabGermanium);
export {
  ElementsTabGermanium,
  ElementsTabsGermanium
};
